<template>
  <div class="multi-line-chart">
    <div class="section-header">
      <h3>{{ title }}</h3>
    </div>

    <div v-if="series.length" class="legend">
      <span
        v-for="(s, idx) in series"
        :key="idx"
        class="legend-item"
      >
        <i class="legend-dot" :style="{ background: colors[idx % colors.length] }"></i>
        {{ s.name }}
      </span>
    </div>

    <div class="chart-body">
      <svg viewBox="0 0 500 300" class="chart-svg" preserveAspectRatio="xMidYMid meet">
        <!-- Y grid lines -->
        <line
          v-for="(g, gi) in yGridLines"
          :key="'yg' + gi"
          :x1="padLeft" :y1="g" :x2="500 - padRight" :y2="g"
          stroke="rgba(99,179,237,0.08)"
          stroke-width="1"
        />

        <!-- Y labels -->
        <text
          v-for="(g, gi) in yGridLines"
          :key="'yl' + gi"
          :x="padLeft - 6"
          :y="g + 4"
          text-anchor="end"
          fill="#718096"
          font-size="10"
        >{{ yLabels[gi] }}</text>

        <!-- X labels -->
        <text
          v-for="(label, xi) in xLabels"
          :key="'xl' + xi"
          :x="padLeft + (xi * xStep)"
          :y="chartH + padTop + 20"
          text-anchor="middle"
          fill="#718096"
          font-size="9"
        >{{ label }}</text>

        <!-- Lines -->
        <polyline
          v-for="(pts, si) in linePoints"
          :key="'line' + si"
          :points="pts"
          fill="none"
          :stroke="colors[si % colors.length]"
          stroke-width="2"
          stroke-linejoin="round"
        />

        <!-- Dots -->
        <g v-for="(dots, si) in dotPoints" :key="'dots' + si">
          <circle
            v-for="(d, di) in dots"
            :key="di"
            :cx="d.x"
            :cy="d.y"
            r="3"
            :fill="colors[si % colors.length]"
          />
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
const COLORS = ['#63b3ed', '#48bb78', '#f6ad55', '#fc8181', '#b794f4', '#f687b3', '#68d391', '#76e4f7']

export default {
  name: 'MultiLineChart',
  props: {
    title: {
      type: String,
      default: ''
    },
    series: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      padLeft: 42,
      padRight: 12,
      padTop: 14,
      padBottom: 36,
      colors: COLORS
    }
  },
  computed: {
    chartW() {
      return 500 - this.padLeft - this.padRight
    },
    chartH() {
      return 300 - this.padTop - this.padBottom
    },
    xLabels() {
      if (!this.series.length) return []
      // union all x values from all series, dedupe and sort
      const set = new Set()
      this.series.forEach(s => {
        (s.x || []).forEach(v => set.add(v))
      })
      return [...set]
    },
    xStep() {
      if (this.xLabels.length <= 1) return this.chartW / 2
      return this.chartW / (this.xLabels.length - 1)
    },
    yMin() {
      if (!this.series.length) return 0
      let min = Infinity
      this.series.forEach(s => {
        (s.y || []).forEach(v => {
          if (v < min) min = v
        })
      })
      return min === Infinity ? 0 : Math.floor(min)
    },
    yMax() {
      if (!this.series.length) return 100
      let max = -Infinity
      this.series.forEach(s => {
        (s.y || []).forEach(v => {
          if (v > max) max = v
        })
      })
      return max === -Infinity ? 100 : Math.ceil(max)
    },
    yGridLines() {
      const lines = []
      const count = 5
      for (let i = 0; i < count; i++) {
        const y = this.padTop + (this.chartH / (count - 1)) * i
        lines.push(y)
      }
      return lines
    },
    yLabels() {
      const labels = []
      const count = 5
      const range = this.yMax - this.yMin || 1
      for (let i = 0; i < count; i++) {
        const val = this.yMax - (range / (count - 1)) * i
        labels.push(Number.isInteger(val) ? val : val.toFixed(1))
      }
      return labels
    },
    linePoints() {
      return this.series.map(s => {
        const xArr = s.x || []
        const yArr = s.y || []
        if (!xArr.length) return ''
        const range = this.yMax - this.yMin || 1
        return xArr.map((xv, i) => {
          const xi = this.xLabels.indexOf(xv)
          const x = this.padLeft + xi * this.xStep
          const y = this.padTop + this.chartH - ((yArr[i] - this.yMin) / range) * this.chartH
          return `${x},${y}`
        }).join(' ')
      })
    },
    dotPoints() {
      return this.series.map(s => {
        const xArr = s.x || []
        const yArr = s.y || []
        const range = this.yMax - this.yMin || 1
        return xArr.map((xv, i) => {
          const xi = this.xLabels.indexOf(xv)
          return {
            x: this.padLeft + xi * this.xStep,
            y: this.padTop + this.chartH - ((yArr[i] - this.yMin) / range) * this.chartH
          }
        })
      })
    }
  }
}
</script>

<style scoped>
.multi-line-chart {
  height: 100%;
  background: rgba(26, 54, 93, 0.6);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  flex-shrink: 0;
}

h3 {
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.legend-item {
  font-size: 13px;
  color: #a0aec0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.chart-body {
  flex: 1;
  min-height: 0;
}

.chart-svg {
  width: 100%;
  height: 100%;
}
</style>
